(() => {
  window.__giftAutoAddEnabled = false; // <-- Flag for custom free gift add to cart. Disabled for Shopflo by LG-HRK, 20260419
  if (!window.__giftAutoAddEnabled) return;
  // ============================================================
  // SETTINGS — update these if variant IDs ever change
  // ============================================================
  const BRUSH_VARIANT_ID        = 52317484450099; // Free Brush
  const GIFT_VARIANT_ID         = 53097368060211; // Free Makeup Bag
  const PAID_MAKEUP_VARIANT_ID  = 53125013209395; // Paid Makeup Bag (blocks free bag)

  // Trigger rules
  const BRUSH_EXCLUDE_TYPE      = 'Combo';  // Combos are excluded from brush trigger
  const BRUSH_TRIGGER_TYPES     = ['lipstick', 'eyepaint', 'eye paint']; // Add free brush for these
  const BAG_TRIGGER_TYPE        = 'Combo';  // Combos trigger free makeup bag

  // Line item property to mark our auto-gifts (prevents confusing them with paid items)
  const GIFT_PROPERTY_KEY       = '_auto_gift';
  const GIFT_PROPERTY_VALUE     = 'true';

  // ============================================================
  // STATE
  // ============================================================
  let syncing = false;     // prevents concurrent syncGifts() calls
  let isGiftOp = false;    // flags our own cart mutations so fetch interceptor ignores them
  let refreshTimer = null;

  // ============================================================
  // CART HELPERS
  // ============================================================
  async function getCart() {
    const r = await fetch('/cart.js', { credentials: 'same-origin' });
    return r.json();
  }

  // ── Brush logic ──────────────────────────────────────────────
  // Qualifies when cart has at least one Lipstick or Eyepaint product that is not a Combo
  function brushQualifies(cart) {
    return cart.items.some((item) => {
      const productType = String(item.product_type || '').trim().toLowerCase();
      const title = String(item.product_title || item.title || '').trim().toLowerCase();

      // Exclude combos
      if (productType === BRUSH_EXCLUDE_TYPE.toLowerCase()) {
        return false;
      }

      // Match by product type
      if (BRUSH_TRIGGER_TYPES.includes(productType)) {
        return true;
      }

      // Fallback match by title
      if (
        title.includes('lipstick') ||
        title.includes('eye paint') ||
        title.includes('eyepaint')
      ) {
        return true;
      }

      return false;
    });
  }

  function findBrushLine(cart) {
    return cart.items.findIndex(
      (item) =>
        item.variant_id === BRUSH_VARIANT_ID &&
        item.properties &&
        item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE
    );
  }

  // ── Makeup bag logic ─────────────────────────────────────────
  // Qualifies when cart has a Combo AND the paid makeup bag is NOT in cart
  function bagQualifies(cart) {
    const hasCombo = cart.items.some(
      (item) => (item.product_type || '').toLowerCase() === BAG_TRIGGER_TYPE.toLowerCase()
    );

    const hasPaidBag = cart.items.some(
      (item) =>
        item.variant_id === PAID_MAKEUP_VARIANT_ID &&
        !(item.properties && item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE)
    );

    return hasCombo && !hasPaidBag;
  }

  function findBagLine(cart) {
    return cart.items.findIndex(
      (item) =>
        item.variant_id === GIFT_VARIANT_ID &&
        item.properties &&
        item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE
    );
  }

  // ============================================================
  // GIFT ADD / REMOVE
  // All flagged with isGiftOp so fetch interceptor skips them
  // Operations are sequential (awaited) — no concurrent cart mutations
  // ============================================================
  async function addGift(variantId) {
    isGiftOp = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { [GIFT_PROPERTY_KEY]: GIFT_PROPERTY_VALUE },
        }),
      });

      if (!res.ok) throw new Error(`gift add failed for variant ${variantId}`);
    } finally {
      isGiftOp = false;
    }
  }

  async function removeGift(lineIndex) {
    // lineIndex is 0-based — Shopify /cart/change.js uses 1-based lines
    isGiftOp = true;
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ line: lineIndex + 1, quantity: 0 }),
      });

      if (!res.ok) throw new Error('gift remove failed');
    } finally {
      isGiftOp = false;
    }
  }

  // ============================================================
  // UI REFRESH — re-renders cart-drawer + bubble via sections API
  // ============================================================
  async function refreshCartUI() {
    try {
      const root = window.Shopify?.routes?.root ?? '/';

      const drawerEl = document.querySelector('cart-drawer');
      const wasOpen =
        drawerEl &&
        (drawerEl.classList.contains('active') ||
          document.body.classList.contains('overflow-hidden'));

      const res = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, {
        credentials: 'same-origin',
      });
      const sections = await res.json();

      // ── cart-drawer ──────────────────────────────────────────
      if (sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newInner = doc.querySelector('#CartDrawer');
        const curInner = document.querySelector('#CartDrawer');
        if (newInner && curInner) curInner.innerHTML = newInner.innerHTML;

        const newWrapper = doc.querySelector('cart-drawer');
        const curWrapper = document.querySelector('cart-drawer');
        if (newWrapper && curWrapper) {
          curWrapper.classList.toggle('is-empty', newWrapper.classList.contains('is-empty'));
        }
      }

      // ── cart-icon-bubble ─────────────────────────────────────
      // Only update innerHTML — never replace the element (prevents icon shrinking)
      if (sections['cart-icon-bubble']) {
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.querySelector('#cart-icon-bubble');
        const curBubble = document.querySelector('#cart-icon-bubble');
        if (newBubble && curBubble) curBubble.innerHTML = newBubble.innerHTML;
      }

      // ── re-open drawer if it was open ────────────────────────
      if (wasOpen) {
        setTimeout(() => {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer) return;
          drawer.classList.add('active');
          document.body.classList.add('overflow-hidden');
        }, 50);
      }
    } catch (e) {
      console.warn('[gift-auto-add] refreshCartUI error', e);
    }
  }

  function scheduleRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refreshCartUI, 400);
  }

  // ============================================================
  // CORE SYNC LOGIC — handles BOTH gifts sequentially
  // Sequential awaits mean we never have two concurrent cart mutations.
  // ============================================================
  async function syncGifts() {
    // Hold off if a user-initiated delete is still in progress
    if (window.__cartDeleteInProgress) {
      clearTimeout(window.__giftSyncTimer);
      window.__giftSyncTimer = setTimeout(syncGifts, 300);
      return;
    }

    if (syncing) return;
    syncing = true;

    try {
      // ── Step 1: Get fresh cart state ─────────────────────────
      let cart = await getCart();

      // ── Step 2: Handle free BRUSH ────────────────────────────
      const brushNeeded = brushQualifies(cart);
      const brushLineIdx = findBrushLine(cart);
      const hasBrush = brushLineIdx !== -1;

      if (brushNeeded && !hasBrush) {
        await addGift(BRUSH_VARIANT_ID);
        // Refresh cart state after mutation so bag check uses accurate line indices
        cart = await getCart();

      } else if (!brushNeeded && hasBrush) {
        await removeGift(brushLineIdx);
        await new Promise((r) => setTimeout(r, 200)); // let Shopify settle
        cart = await getCart();
      }

      // ── Step 3: Handle free MAKEUP BAG ──────────────────────
      // Fresh cart is used here — indices are accurate after brush op above
      const bagNeeded = bagQualifies(cart);
      const bagLineIdx = findBagLine(cart);
      const hasBag = bagLineIdx !== -1;

      if (bagNeeded && !hasBag) {
        await addGift(GIFT_VARIANT_ID);
        scheduleRefresh();

      } else if (!bagNeeded && hasBag) {
        await removeGift(bagLineIdx);
        await new Promise((r) => setTimeout(r, 200));
        scheduleRefresh();

      } else if (brushNeeded !== hasBrush) {
        // Brush changed but bag was already correct — still need a UI refresh
        scheduleRefresh();
      }

    } catch (e) {
      console.warn('[gift-auto-add] syncGifts error', e);
    } finally {
      syncing = false;
    }
  }

  // ============================================================
  // FETCH INTERCEPTOR — triggers syncGifts on cart mutations
  // Skips: our own gift ops, section fetches, read-only cart.js calls
  // ============================================================
  const _origFetch = window.fetch;
  window.fetch = async function (...args) {
    const response = await _origFetch.apply(this, args);

    try {
      const url = args[0] instanceof Request ? args[0].url : String(args[0]);

      if (isGiftOp) return response; // our own gift add/remove
      if (window.__upsellOp) return response; // upsell add from cart-drawer.liquid
      if (url.includes('sections=')) return response; // section re-render fetches
      if (url.includes('/cart.js') && !url.includes('/cart/')) return response; // read-only

      const isCartMutation =
        url.includes('/cart/add') ||
        url.includes('/cart/change') ||
        url.includes('/cart/update') ||
        url.includes('/cart/clear');

      if (isCartMutation) {
        clearTimeout(window.__giftSyncTimer);
        // Use longer delay during deletes so Dawn + any other scripts settle first
        const delay = window.__cartDeleteInProgress ? 800 : 500;
        window.__giftSyncTimer = setTimeout(syncGifts, delay);
      }
    } catch (_) {}

    return response;
  };

  // ============================================================
  // TRIGGER: page load
  // ============================================================
  document.addEventListener('DOMContentLoaded', syncGifts);
})();